import { defineStore } from 'pinia';
import type { CartType } from '~/constants/types/cart';
import CartData from '~/models/cart-data';
import { useConfigStore } from '~/store/config';
import useApiFetch from '~/composables/useApiFetch';
import type Product from '~/models/product';
import { pushToDataLayer } from '~/composables/useTracking';
import type CartItem from '~/models/cart-item';

export const useCartStore = defineStore(
  'cartStore', {
    state: () => ({
      _cartData: {} as CartType,
      showAddedItem: false,
      addedItem: null as { product: Product, itemId: string, quantity: number } | null,
      showCartMenu: false,
      cartIsUpdating: false,
      cartInitialized: false,
    }),
    actions: {
      async initialCart() {
        const config = useConfigStore();
        const configMarket = config.market || {};
        const { country, language, market, pricelist } = configMarket;
        const { centraPut, handleApiError } = useApiFetch();

        const res = await centraPut<CartType>('/selection', {
          country,
          language,
        });
        if (res) {
          //console.log('the selection in initial: ' + JSON.stringify(res));
          this._cartData = res;
          this.cartInitialized = true;
          if (res.selection.centraCheckoutScript != '') {
            const s = document.createElement('script');
            s.type = 'text/javascript';
            const code = res.selection.centraCheckoutScript;
            try {
              s.appendChild(document.createTextNode(code));
              document.body.appendChild(s);
            } catch (e) {
              s.text = code;
              document.body.appendChild(s);
            }
            console.log('Added script');
            //getCentraCheckoutScript(res.selection.centraCheckoutScript);
            //console.log('checkoutScript: ' + res.selection.centraCheckoutScript);
            //document.addEventListener('centra_checkout_callback', function(origdata) {
            //  console.log('centra_checkout_callback! '+origdata);
            //  var url = '{your-endpoint-which-does-PUT-checkout/payment-fields}'
            //  $.ajax(url, {
            //    method:"POST",
            //    data: origdata.detail,
            //    success: function(data) {
            //    // Ajax update data from response
            //      CentraCheckout.resume(origdata.detail.additionalFields.suspendIgnore);
            //    },
            //  });
            //});
            //centra_checkout_callback('test');
          }
        } else {
          handleApiError();
        }
      },
      async fetchCart() {
        const { centraGet, handleApiError } = useApiFetch();
        const res = await centraGet<CartType>('/selection');
        if (res) {
          this._cartData = res;
        } else {
          handleApiError();
        }
      },

      async addToCart(payload: { product: Product, itemId: string, quantity: number }) {
        const { centraPost } = useApiFetch();
        const res = await centraPost<CartType>(
          `/items/${payload.itemId}/quantity/${payload.quantity}`
        );
        if (res) {
          this._cartData = res;
          this.addedItem = {
            product: payload.product,
            itemId: payload.itemId,
            quantity: payload.quantity,
          };
          this.showAddedItem = true;

          // Tracking
          const trackingPayload = {
            event: 'productAddToCart',
            ecommerce: {
              currencyCode: this.cartData.currency,
              add: {
                products: [
                  {
                    id: payload.product.sku,
                    feedId: payload.product.sku,
                    ...payload.product.gtmProductValues,
                    quantity: payload.quantity,
                  },
                ],
              },
            },
          };
          pushToDataLayer(trackingPayload);

        } else {
          /**
           * @todo - error hanling
           */
        }
      },

      async addGiftCardToCart(giftCertificate: string) {
        const { centraPost, handleApiError } = useApiFetch();
        const res = await centraPost<CartType>(
          `/items/gift-certificates/${giftCertificate}`
        );
        if (res) {
          this._cartData = res;
          return true;
        } else {
          console.error();
          handleApiError();
          return false;
        }
      },

      async modifyItemQuantity({ line, quantity, item }:{ line: string, quantity: number, item: CartItem }) {

        this.cartIsUpdating = true;
        const originalQuantity = item.quantity;
        const diff = Math.abs(item.quantity - quantity);
        const { centraPut } = useApiFetch();
        const res = await centraPut<CartType>(`/lines/${line}/quantity/${quantity}`);
        if (res) {
          this._cartData = res;

          if (quantity !== originalQuantity) {
            const payload = {
              event: quantity < originalQuantity ? 'productRemoveFromCart':'productAddToCart',
              ecommerce: {
                currencyCode: this.cartData.currency,
                remove: {
                  products: [
                    {
                      id: item.sku,
                      feedId: item.sku,
                      ...item.product.gtmProductValues,
                      quantity: diff,
                    },
                  ],
                },
              },
            };
            pushToDataLayer(payload);
          }

        }
        this.cartIsUpdating = false;
      },
      async deleteFromCart(payload: { line: string, item?: CartItem }) {
        this.cartIsUpdating = true;
        const { centraDelete } = useApiFetch();
        const res = await centraDelete<CartType>(`/lines/${payload.line}`);
        if (res) {
          this._cartData = res;
          if (payload.item) {
            const trackingPayload = {
              event: 'productRemoveFromCart',
              ecommerce: {
                currencyCode: this.cartData.currency,
                remove: {
                  products: [
                    {
                      id: payload.item.sku,
                      feedId: payload.item.sku,
                      ...payload.item.product.gtmProductValues,
                    },
                  ],
                },
              },
            };
            pushToDataLayer(trackingPayload);
          }
        }
        this.cartIsUpdating = false;
      },
      async updatePaymentFields(body: object) {
        const { centraPut } = useApiFetch();
        const res = await centraPut<CartType>('/payment-fields', body);
        if (res) {
          this._cartData = res;
        }
      },
    },
    getters: {
      cartData: (state) => new CartData(state._cartData),
    },
  }
);
